import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Input, Modal, Skeleton, Spin, Table, Tabs, message } from "antd";
import {
  AppstoreOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  FolderOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ADAIcon from "../../../../icons/ADAIcon";
import RemoveFromBrandIcon from "../../../../icons/RemoveFromBrandIcon";
import { useRecoilValue } from "recoil";
import { memberState } from "../../../../../state/atoms";
import AssetApi from "../../../../../api/AssetApi";
import {
  AssetDto as GeneratedAssetDto,
  BulkUploadResultDto,
  FileUploadResult,
} from "../../../../../generated/dashboard-client";
import { env } from "../../../../../env/env";
import { AssetLibraryModal } from "../AssetLibraryModal";
import { AddToBrandKitModal } from "../AddToBrandKitModal";
import { ACCEPTED_INPUT_ACCEPT, isAcceptedFile } from "../assetUpload";
import {
  MoveCategoryModal,
  buildAssetMenuItems,
  uiCategoryFor,
  useAssetImageDims,
} from "../assetActionMenu";
import { useAssetDrdAttributes, AssetAttributeCell } from "../assetDrdAttribute";
import "./AssetsTab.scss";

type AssetDto = GeneratedAssetDto & { brandKitNames?: string[] };

const CATEGORIES = [
  { key: "", label: "All", singular: "Asset" },
  { key: "LOGOS", label: "Logos", singular: "Logo" },
  { key: "FONTS", label: "Fonts", singular: "Font" },
  { key: "ICONS", label: "Icons", singular: "Icon" },
  { key: "GRAPHICS", label: "Graphics", singular: "Graphic" },
];

const BACKEND_CATEGORIES: Record<string, string[]> = {
  LOGOS: ["LOGOS"],
  FONTS: ["FONTS"],
  ICONS: ["ICONS"],
  GRAPHICS: ["GRAPHICS", "PHOTOS"],
};

const ALL_BACKEND_CATEGORIES = ["LOGOS", "FONTS", "ICONS", "GRAPHICS", "PHOTOS"];

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function displayName(asset: AssetDto): string {
  const name = asset.name || "";
  return name.replace(/-[a-zA-Z0-9]{8,}(\.[^.]+)?$/, "$1");
}

const FONT_PREVIEW_TEXT = "The quick brown fox";

function FontPreview({ asset }: { asset: AssetDto }) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const family = useMemo(
    () => `asset-font-${(asset.id || asset.path || "").replace(/[^a-zA-Z0-9]/g, "")}`,
    [asset.id, asset.path]
  );

  useEffect(() => {
    if (!asset.path || typeof (window as any).FontFace === "undefined") {
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    const face = new FontFace(family, `url("${env.s3Path}/${asset.path}")`);
    face
      .load()
      .then((loaded) => {
        if (cancelled) return;
        (document as any).fonts.add(loaded);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [family, asset.path]);

  if (status === "error") {
    return <span className="asset-font-preview-unavailable">Preview unavailable</span>;
  }
  return (
    <span
      className="asset-font-preview"
      style={status === "ready" ? { fontFamily: `"${family}"` } : undefined}
    >
      {FONT_PREVIEW_TEXT}
    </span>
  );
}

interface AssetsTabProps {
  brandKitId: string;
  brandKitName?: string;
  onCountDelta?: (delta: number) => void;
}

export function AssetsTab({ brandKitId, brandKitName, onCountDelta }: AssetsTabProps) {
  const member = useRecoilValue(memberState);
  const teamId = member?.teamId || "";

  // DRD accounts can map each uploaded icon to a DiningRD attribute (only in the Icons category).
  const drdActive = !!(member as any)?.diningRDIntegration;

  const [assets, setAssets] = useState<AssetDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveModalIds, setMoveModalIds] = useState<string[]>([]);
  const [removeAsset, setRemoveAsset] = useState<AssetDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [addToKitOpen, setAddToKitOpen] = useState(false);
  const [addToKitAssets, setAddToKitAssets] = useState<AssetDto[]>([]);
  const [addToKitFromBulk, setAddToKitFromBulk] = useState(false);

  function openAddToKit(targets: AssetDto[], fromBulk: boolean) {
    if (targets.length === 0) return;
    setAddToKitAssets(targets);
    setAddToKitFromBulk(fromBulk);
    setAddToKitOpen(true);
  }

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [altTextModal, setAltTextModal] = useState(false);
  const [altTextValue, setAltTextValue] = useState("");
  const [altTextSaving, setAltTextSaving] = useState(false);
  const [currentAsset, setCurrentAsset] = useState<AssetDto | null>(null);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef<any>(null);

  // Per-attribute DiningRD mapping now lives solely in the "Icon Attributes" tab, so the
  // Uploads table stays a plain asset table (no Attribute column) even for the Icons category.
  const showAttribute = false;
  const { attributeForPath, setAttribute, clearAttribute } = useAssetDrdAttributes(brandKitId, drdActive);

  function startRename(asset: AssetDto) {
    if (!asset.id) return;
    setRenamingId(asset.id);
    setRenameValue(displayName(asset));
    setTimeout(() => renameInputRef.current?.select(), 0);
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameValue("");
  }

  function handleRename() {
    if (renameSaving) return;
    const name = renameValue.trim();
    const asset = assets.find((a) => a.id === renamingId);
    if (!asset?.id || !name || name === displayName(asset)) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    AssetApi.rename(asset.id, name)
      .then((resp) => {
        setAssets((prev) => prev.map((a) => (a.id === resp.data.id ? { ...a, ...resp.data } : a)));
        setRenamingId(null);
        setRenameValue("");
        message.success("Asset renamed");
      })
      .catch(() => message.error("Failed to rename asset"))
      .finally(() => setRenameSaving(false));
  }

  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeCategoryRef = useRef(activeCategory);
  activeCategoryRef.current = activeCategory;

  const onCountDeltaRef = useRef(onCountDelta);
  onCountDeltaRef.current = onCountDelta;

  const load = useCallback(() => {
    if (!brandKitId) return;
    setLoading(true);
    const cats = activeCategory
      ? BACKEND_CATEGORIES[activeCategory] || [activeCategory]
      : undefined;
    AssetApi.listByBrandKit(brandKitId, cats)
      .then((resp) => setAssets(resp.data || []))
      .catch(() => message.error("Failed to load assets"))
      .finally(() => setLoading(false));
  }, [brandKitId, activeCategory]);

  useEffect(load, [load]);

  const imgDims = useAssetImageDims(assets);

  const filteredAssets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) => (a.name || "").toLowerCase().includes(q));
  }, [assets, searchQuery]);

  function uploadFiles(files: File[]) {
    if (!teamId || files.length === 0) return;
    const category = activeCategoryRef.current || undefined;
    setUploading((n) => n + files.length);
    AssetApi.bulkUpload(teamId, files, category)
      .then((resp) => {
        const result: BulkUploadResultDto = resp.data;
        const results = result.results || [];
        const uploaded = results
          .filter((r: FileUploadResult) => r.success && r.asset)
          .map((r: FileUploadResult) => r.asset!);

        if (uploaded.length > 0 && brandKitId) {
          const ids = uploaded.map((a) => a.id!).filter(Boolean);
          AssetApi.assignToBrand(ids, brandKitId)
            .then(() => {
              setAssets((prev) => [...uploaded, ...prev]);
              onCountDeltaRef.current?.(uploaded.length);
            })
            .catch(() => {
              message.error("Uploaded, but failed to link to this brand");
              setAssets((prev) => [...uploaded, ...prev]);
            });
        }

        if (result.successCount && result.successCount > 0) {
          message.success(
            `${result.successCount} file${result.successCount > 1 ? "s" : ""} uploaded`
          );
        }
        results
          .filter((r: FileUploadResult) => !r.success)
          .forEach((r: FileUploadResult) =>
            message.error(`Failed to upload "${r.fileName}": ${r.error}`)
          );
      })
      .catch(() => message.error("Upload failed"))
      .finally(() => setUploading((n) => n - files.length));
  }

  function onFilesSelected(files: FileList | null) {
    if (!files) return;
    const all = Array.from(files);
    const unsupported = all.filter((f) => !isAcceptedFile(f));
    const supported = all.filter((f) => isAcceptedFile(f));
    if (unsupported.length) {
      message.error(
        unsupported.length === 1
          ? `"${unsupported[0].name}" is not a supported file type`
          : `${unsupported.length} files are not a supported file type`
      );
    }
    const accepted = supported.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    const tooLarge = supported.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooLarge.length) {
      message.error(
        tooLarge.length === 1
          ? `"${tooLarge[0].name}" exceeds the 10MB upload limit`
          : `${tooLarge.length} files exceed the 10MB upload limit`
      );
    }
    if (accepted.length) uploadFiles(accepted);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    onFilesSelected(e.dataTransfer.files);
  }

  function handleRemoveFromBrandKit(asset: AssetDto) {
    setRemoveAsset(asset);
  }

  function closeRemoveModal() {
    if (removing) return;
    setRemoveAsset(null);
  }

  function confirmRemoveFromBrandKit() {
    if (!removeAsset) return;
    const id = removeAsset.id!;
    setRemoving(true);
    AssetApi.unassignFromBrand([id], brandKitId)
      .then(() => {
        setAssets((prev) => prev.filter((a) => a.id !== id));
        onCountDeltaRef.current?.(-1);
        message.success("Removed from Brand Kit");
        setRemoveAsset(null);
      })
      .catch(() => message.error("Failed to remove asset"))
      .finally(() => setRemoving(false));
  }

  function handleAltText(asset: AssetDto) {
    setCurrentAsset(asset);
    setAltTextValue(asset.altText || "");
    setAltTextModal(true);
  }

  function saveAltText() {
    if (!currentAsset) return;
    setAltTextSaving(true);
    AssetApi.updateAltText(currentAsset.id!, altTextValue)
      .then((resp) => {
        setAssets((prev) => prev.map((a) => (a.id === resp.data.id ? resp.data : a)));
        setAltTextModal(false);
      })
      .catch(() => message.error("Failed to update ADA text"))
      .finally(() => setAltTextSaving(false));
  }

  function openMoveModal(ids: string[]) {
    if (ids.length === 0) return;
    setMoveModalIds(ids);
    setMoveModalOpen(true);
  }

  function closeMoveModal() {
    setMoveModalOpen(false);
    setMoveModalIds([]);
  }

  function handleMovedFromModal(updated: AssetDto[], category: string) {
    const updatedById = new Map(updated.map((a) => [a.id!, a]));
    const activeBackendCats = activeCategory
      ? BACKEND_CATEGORIES[activeCategory] || [activeCategory]
      : ALL_BACKEND_CATEGORIES;
    if (!activeBackendCats.includes(category)) {
      setAssets((prev) => prev.filter((a) => !updatedById.has(a.id!)));
    } else {
      setAssets((prev) =>
        prev.map((a) => (updatedById.has(a.id!) ? updatedById.get(a.id!)! : a))
      );
    }
    setSelectedRowKeys((prev) => prev.filter((k) => !updatedById.has(k as string)));
    closeMoveModal();
  }

  const moveExcludeCategory = useMemo(() => {
    if (moveModalIds.length === 0) return undefined;
    const idSet = new Set(moveModalIds);
    const targetUiCats = new Set(
      assets.filter((a) => idSet.has(a.id!)).map((a) => uiCategoryFor(a.category))
    );
    if (targetUiCats.size === 1) {
      return targetUiCats.values().next().value as string;
    }
    return undefined;
  }, [moveModalIds, assets]);

  function handleBulkRemoveFromBrandKit() {
    const ids = selectedRowKeys as string[];
    const noun = `asset${ids.length > 1 ? "s" : ""}`;
    Modal.confirm({
      title: "Remove from Brand Kit?",
      content: `${ids.length} ${noun} will be removed from this Brand Kit but will remain in the Asset Library and any other Brand Kits they belong to. This cannot be undone.`,
      okText: "Remove from Brand Kit",
      okButtonProps: { danger: true },
      onOk: () =>
        AssetApi.unassignFromBrand(ids, brandKitId)
          .then(() => {
            setAssets((prev) => prev.filter((a) => !ids.includes(a.id!)));
            setSelectedRowKeys([]);
            onCountDeltaRef.current?.(-ids.length);
            message.success(`${ids.length} ${noun} removed from Brand Kit`);
          })
          .catch(() => message.error("Failed to remove assets from Brand Kit")),
    });
  }

  function handleDownload(asset: AssetDto) {
    const url = `${env.s3Path}/${asset.path}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = asset.name || "";
    a.target = "_blank";
    a.click();
  }

  function handleBulkDownload() {
    const ids = selectedRowKeys as string[];
    if (ids.length === 0 || bulkDownloading) return;
    setBulkDownloading(true);
    AssetApi.bulkDownload(ids)
      .then((resp) => {
        const blob = new Blob([resp.data], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "assets.zip";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => message.error("Failed to download assets"))
      .finally(() => setBulkDownloading(false));
  }

  function actionMenu(asset: AssetDto) {
    return {
      items: buildAssetMenuItems({
        asset,
        dims: asset.id ? imgDims[asset.id] : undefined,
        onAda: handleAltText,
        onDownload: handleDownload,
        onMove: (a) => openMoveModal([a.id!]),
        onAddToBrandKit: (a) => openAddToKit([a], false),
        onRemove: handleRemoveFromBrandKit,
        removeLabel: "Remove from Brand Kit",
        removeIcon: <RemoveFromBrandIcon />,
      }),
    };
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      sorter: (a: AssetDto, b: AssetDto) => (a.name || "").localeCompare(b.name || ""),
      render: (_: string, record: AssetDto) => {
        const isRenaming = renamingId === record.id;
        return (
          <div className="asset-name-cell">
            {isRenaming ? (
              <span
                className="asset-name-rename"
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  ref={renameInputRef}
                  className="asset-name-rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onPressEnter={handleRename}
                  onKeyDown={(e) => e.key === "Escape" && cancelRename()}
                  onBlur={handleRename}
                  disabled={renameSaving}
                />
              </span>
            ) : (
              <span className="asset-name-text-wrap">
                <span className="asset-name-text">{displayName(record)}</span>
                <EditOutlined
                  className="asset-name-edit-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    startRename(record);
                  }}
                />
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Preview",
      key: "preview",
      width: 260,
      render: (_: unknown, record: AssetDto) => {
        if (record.category === "FONTS") return <FontPreview asset={record} />;
        return record.fileType?.startsWith("image/") ? (
          <div
            className="asset-thumb"
            style={{ backgroundImage: `url("${env.s3Path}/${record.path}")` }}
          />
        ) : (
          <div className="asset-thumb asset-thumb-file">
            {(record.name || "").split(".").pop()?.toUpperCase()}
          </div>
        );
      },
    },
    ...(showAttribute
      ? [
          {
            title: "Attribute",
            key: "attribute",
            width: 200,
            render: (_: unknown, record: AssetDto) => (
              <AssetAttributeCell
                attribute={attributeForPath(record.path)}
                onSelect={(attr) => setAttribute(record.path!, record.altText, attr)}
                onClear={() => clearAttribute(record.path!)}
              />
            ),
          },
        ]
      : []),
    {
      title: "Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 100,
      render: (size: number) => formatFileSize(size),
      sorter: (a: AssetDto, b: AssetDto) => (a.fileSize || 0) - (b.fileSize || 0),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: any, record: AssetDto) => (
        <Dropdown
          menu={actionMenu(record)}
          trigger={["click"]}
          overlayClassName="asset-action-menu"
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="assets-tab">
      <Spin spinning={uploading > 0} tip="Uploading...">
        <div
          className={`asset-upload-area ${dragging ? "dragging" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <CloudUploadOutlined className="upload-icon" />
          <p>
            Drag and drop your logos, fonts, icons, photos and graphics here or{" "}
            <a onClick={() => fileInputRef.current?.click()}>
              select a file from your computer
            </a>
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_INPUT_ACCEPT}
            style={{ display: "none" }}
            onChange={(e) => {
              onFilesSelected(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </Spin>

      <div className="asset-toolbar">
        <Tabs
          activeKey={activeCategory}
          onChange={(key) => setActiveCategory(key)}
          items={CATEGORIES.map((c) => ({ key: c.key, label: c.label }))}
        />
        <div className="asset-toolbar-right">
          <Input
            className="asset-search"
            size="small"
            placeholder="Search assets..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />
          <Button
            className="asset-add-from-library-btn"
            icon={<AppstoreOutlined />}
            onClick={() => setLibraryOpen(true)}
          >
            Add from asset library
          </Button>
        </div>
      </div>

      <div
        className={`asset-bulk-bar${selectedRowKeys.length > 0 ? " visible" : ""}`}
      >
        <Button
          type="text"
          className="bulk-close-btn"
          icon={<CloseOutlined />}
          onClick={() => setSelectedRowKeys([])}
          title="Clear selection"
        />
        <h4>
          {selectedRowKeys.length} item{selectedRowKeys.length === 1 ? "" : "s"} selected
        </h4>
        <div className="asset-bulk-actions">
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={handleBulkDownload}
            loading={bulkDownloading}
            title="Download"
          />
          <Button
            type="text"
            icon={<FolderOutlined />}
            onClick={() => openMoveModal(selectedRowKeys as string[])}
            title="Move to Category"
          />
          <Button
            type="text"
            icon={<PlusCircleOutlined />}
            onClick={() =>
              openAddToKit(
                assets.filter((a) => selectedRowKeys.includes(a.id!)),
                true
              )
            }
            title="Add to Other Brand Kit"
          />
          <Button
            type="text"
            className="bulk-delete-btn"
            icon={<RemoveFromBrandIcon />}
            onClick={handleBulkRemoveFromBrandKit}
            title="Remove from Brand Kit"
          />
        </div>
      </div>

      {loading ? (
        <Skeleton active />
      ) : (
        <Table
          className={`asset-table${selectedRowKeys.length > 0 ? " has-bulk-bar" : ""}`}
          dataSource={filteredAssets}
          columns={columns}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: "No assets found" }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
        />
      )}

      <Modal
        className="asset-alt-text-modal one"
        open={altTextModal}
        onCancel={() => setAltTextModal(false)}
        footer={null}
        width={500}
      >
        {currentAsset && currentAsset.fileType?.startsWith("image/") && (
          <div className="alt-text-preview">
            <img src={`${env.s3Path}/${currentAsset.path}`} alt={currentAsset.name} />
          </div>
        )}
        <h3><ADAIcon /> Accessibility Text</h3>
        <p>Image description necessary for ADA compliance and screen readers</p>
        <textarea
          placeholder="Description 125 chars max"
          maxLength={125}
          value={altTextValue}
          onChange={(e) => setAltTextValue(e.target.value)}
        />
        <footer>
          <Button
            type="primary"
            disabled={!altTextValue}
            loading={altTextSaving}
            onClick={saveAltText}
          >
            Done
          </Button>
        </footer>
      </Modal>

      <AssetLibraryModal
        open={libraryOpen}
        teamId={teamId}
        brandKitId={brandKitId}
        onClose={() => setLibraryOpen(false)}
        onAssetsAdded={(count) => {
          load();
          if (count > 0) onCountDeltaRef.current?.(count);
        }}
      />

      <MoveCategoryModal
        open={moveModalOpen}
        assetIds={moveModalIds}
        excludeCategory={moveExcludeCategory}
        onCancel={closeMoveModal}
        onMoved={handleMovedFromModal}
      />

      <AddToBrandKitModal
        open={addToKitOpen}
        currentBrandKitId={brandKitId}
        currentBrandKitName={brandKitName}
        assets={addToKitAssets}
        onClose={() => setAddToKitOpen(false)}
        onAdded={() => addToKitFromBulk && setSelectedRowKeys([])}
      />

      <Modal
        className={"clean one"}
        open={!!removeAsset}
        onCancel={closeRemoveModal}
        footer={null}
      >
        <header>
          <h2>Are you sure?</h2>
        </header>

        <p>
          This asset will be removed from <b>{brandKitName || "this Brand Kit"}</b>. This action cannot be undone.
        </p>

        <footer>
          <Button size="large" onClick={closeRemoveModal} disabled={removing}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            onClick={confirmRemoveFromBrandKit}
            loading={removing}
          >
            Remove
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
